import { Link } from "react-router-dom";
import { ArrowRight, ChartColumnIncreasing, Compass, Landmark, Waves } from "lucide-react";
import { siteContent } from "@/data/siteContent";

const investorIcons = [Compass, Landmark, ChartColumnIncreasing];

export default function InvestorPage() {
  return (
    <main className="min-h-screen overflow-x-hidden bg-background text-foreground" data-testid="investor-page">
      <header className="px-4 py-4 md:px-8" data-testid="investor-page-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between border border-border bg-white/80 px-4 py-3 shadow-[0_12px_40px_rgba(15,31,56,0.08)] backdrop-blur-md md:px-6">
          <Link className="flex items-center gap-3" data-testid="investor-page-home-link" to="/">
            <div className="h-9 w-[150px] overflow-hidden md:h-10 md:w-[190px]" data-testid="investor-page-logo-wrapper">
              <img alt={siteContent.logo.alt} className="h-full w-full scale-[1.65] object-contain object-center" data-testid="investor-page-logo-image" src={siteContent.logo.url} />
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link className="hidden text-xs uppercase tracking-[0.2em] text-primary md:inline-flex" data-testid="investor-page-back-link" to="/">
              Back to Home
            </Link>
            <a className="inline-flex items-center rounded-sm bg-primary px-5 py-3 text-sm text-primary-foreground transition duration-300 hover:bg-[#1C3359]" data-testid="investor-page-call-button" href={siteContent.contact.phoneHref}>
              Call Suncoast Homes
            </a>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-6 py-16 md:px-12 lg:px-24 lg:py-24" data-testid="investor-hero-section">
        <p className="text-xs uppercase tracking-[0.24em] text-[#94A69B]" data-testid="investor-hero-overline">
          Investor page
        </p>
        <h1 className="mt-5 max-w-4xl font-serif text-5xl font-light tracking-tight text-primary sm:text-6xl" data-testid="investor-hero-title">
          {siteContent.investorPage.title}
        </h1>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="investor-hero-subtitle">
          {siteContent.investorPage.subtitle}
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="investor-hero-actions">
          <a className="inline-flex h-12 items-center justify-center rounded-sm bg-primary px-8 text-sm uppercase tracking-[0.18em] text-primary-foreground transition duration-300 hover:bg-[#1C3359]" data-testid="investor-hero-email-button" href={siteContent.contact.emailHref}>
            Email Suncoast Homes
          </a>
          <Link className="inline-flex h-12 items-center justify-center rounded-sm border border-primary/20 px-8 text-sm uppercase tracking-[0.18em] text-primary transition duration-300 hover:bg-primary/5" data-testid="investor-hero-homepage-link" to="/">
            View main homepage
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10 md:px-12 lg:px-24 lg:py-12" data-testid="investor-pillar-section">
        <div className="grid gap-4 md:grid-cols-3" data-testid="investor-pillar-grid">
          {siteContent.investorPage.pillars.map((pillar) => (
            <article className="border border-border bg-white p-6" data-testid={`investor-pillar-card-${pillar.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={pillar.title}>
              <p className="font-serif text-3xl font-light text-primary" data-testid={`investor-pillar-title-${pillar.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {pillar.title}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground" data-testid={`investor-pillar-description-${pillar.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {pillar.description}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 md:px-12 lg:px-24 lg:py-16" data-testid="investor-cards-section">
        <div className="grid gap-4 lg:grid-cols-3" data-testid="investor-cards-grid">
          {siteContent.investorPage.cards.map((card, index) => {
            const Icon = investorIcons[index];
            return (
              <article className="border border-border bg-[#f7f6f1] p-6" data-testid={`investor-card-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={card.title}>
                <Icon className="h-6 w-6 text-[#94A69B]" data-testid={`investor-card-icon-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                <p className="mt-4 font-serif text-3xl font-light text-primary" data-testid={`investor-card-title-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {card.title}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground" data-testid={`investor-card-description-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {card.description}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-20 md:px-12 lg:px-24 lg:pb-24" data-testid="investor-corridor-section">
        <div className="border border-border bg-primary p-8 text-white md:p-10" data-testid="investor-corridor-panel">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-white/65" data-testid="investor-corridor-overline">
            <Waves className="h-4 w-4" />
            Beach corridor coverage
          </div>
          <p className="mt-4 max-w-3xl font-serif text-4xl font-light tracking-tight text-white" data-testid="investor-corridor-title">
            Clearwater Beach to Boca Ciega Bay, with a turnkey lens on opportunity.
          </p>
          <div className="mt-6 flex flex-wrap gap-3" data-testid="investor-corridor-tags">
            {siteContent.coastlineAreas.map((area) => (
              <span className="border border-white/15 bg-white/8 px-3 py-2 text-xs uppercase tracking-[0.18em] text-white/78" data-testid={`investor-corridor-tag-${area.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={area}>
                {area}
              </span>
            ))}
          </div>
          <a className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-white" data-testid="investor-corridor-email-link" href={siteContent.contact.emailHref}>
            Start the conversation <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </main>
  );
}